"use client";

import type React from "react";

import { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  TableContainer,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  CheckCircle,
  DoNotDisturb,
  Troubleshoot,
} from "@mui/icons-material";
import type {
  MigrationResponse,
  ApiFile,
  SortDirection,
  SortColumn,
  SourceUploadComment,
  SourceUploadCommentDetail,
} from "../../../../types/migrations";
// } from "../../../../types/migrations"
import { getApiManagerDisplayName } from "../../../../utils/format-utils";
import InfoBox from "../../../common/Information/infoBox";
import CustomTable from "../../../common/Table/customTable";
import { MigrationDistributionChart } from "../../MigrationTool/MigrationDistributionChart";
import {
  getComplexityBGC,
  getComplexityColor,
  getComplexityLabel,
} from "../../MigrationTool/Assessment/ApiDetailsModal";

interface SummaryProps {
  dataResponse: MigrationResponse | any;
  onDownload: (fileType: string, fileName?: string) => void;
  onBackToSetup: () => void;
  resListAssesmnet?: any;
}

const Summary: React.FC<SummaryProps> = ({ dataResponse, onDownload, onBackToSetup, resListAssesmnet }) => {
  // Separate states for each drawer
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);

  const [selectedComments, setSelectedComments] = useState<string[]>([]);
  const [selectedSourceUploadComments, setSelectedSourceUploadComments] = useState<SourceUploadComment[] | null>(null);

  const [selectedApiName, setSelectedApiName] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingTarget, setIsDownloadingTarget] = useState(false);

  // Add sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>("apiName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [sortedFiles, setSortedFiles] = useState<ApiFile[] | any>([]);

  //Variable habilitar deshabilitar refresh de botones en la lista de apis.
  const [isDataMigrateListButtonDisabled, setIsDataMigrateListButtonDisabled] = useState(true);
  const [isDataAssessmentListButtonDisabled, setIsDataAssessmentListButtonDisabled] = useState(false);

  //Header tabla custom table
  const [headersCustomTable, setHeadersCustomTable] = useState<string[]>([]);

  const files: ApiFile[] = useMemo(() => {
    return dataResponse?.content?.migrationResult || [];
  }, [dataResponse]);

  //Get data from localstorage and parse json
  const dataConfigStr: any = localStorage.getItem("configuration");
  const dataConfig = JSON.parse(dataConfigStr);

  // Set objetc aiModel
  const aiModel = {
    provider: getNameConfigurationProvider(dataConfig.content!, dataResponse.content.aiModel!),
    model: getNameConfigurationModel(dataConfig.content!, dataResponse.content.aiModel!),
    region: dataResponse.content.aiModel.region!,
    temperature: dataResponse.content.aiModel.temperature!,
  };

  const [isMigrationSuccess, setIsMigrationSuccess] = useState(dataResponse?.success ?? false);

  const migrationId =
    dataResponse && dataResponse.content
      ? dataResponse?.content?.migrationId!
      : resListAssesmnet?.content?.migrationId!;

  useEffect(() => {
    setIsMigrationSuccess(dataResponse?.success ?? false);
  }, [dataResponse?.success]);

  useEffect(() => {
    setSortedFiles(handleSortByApiName(files).filter((item: any) => item.excluded !== true));
  }, [files, sortColumn, sortDirection]);

  if (!isMigrationSuccess) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, color: "#414141" }}>
          Summary
        </Typography>
        <Typography>
          Migration {dataResponse ? "failed" : "data not available"}: {dataResponse?.error || "No data"}
        </Typography>
      </Paper>
    );
  }

  // Calculate statistics
  const totalApis = files.length;
  const migratedApis = files.filter((api) => api.excluded !== true).filter((file) => file.status === "MIGRATED").length;
  const reviewRequiredApis = files.filter((file) => file.status !== "MIGRATED" && !file.excluded).length;

  const dataAssessments = files
    .filter((api) => api.excluded !== true)
    .filter((item) => item.assessmentContentDto && typeof item.assessmentContentDto.complexity === "number");
  const lowComplexity = dataAssessments.filter((item) => item.assessmentContentDto.complexity === 1).length;
  const midComplexity = dataAssessments.filter((item) => item.assessmentContentDto.complexity === 2).length;
  const highComplexity = dataAssessments.filter((item) => item.assessmentContentDto.complexity === 3).length;

  // Chart data
  const chartData = {
    labels: ["Low Complexity", "Medium Complexity", "High Complexity"],
    values: [lowComplexity, midComplexity, highComplexity],
    colors: [getComplexityBGC(1), getComplexityBGC(2), getComplexityBGC(3)],
  };

  chartData.values = [lowComplexity, midComplexity, highComplexity];
  const dataInfoBox = [
    { title: "Total Apis", value: totalApis, color: "dimgray", bgColor: "pink", Icon: CheckCircle, label: "" },
    { title: "Migrated", value: migratedApis, color: "green", bgColor: "pink", Icon: CheckCircleIcon, label: "" },
    {
      title: "Review Required",
      value: reviewRequiredApis,
      color: "orange",
      bgColor: "pink",
      Icon: WarningIcon,
      // label: "",
    },
    {
      title: "Exclude",
      value: dataResponse.content.migrationRecordsEcluded,
      color: "brown",
      bgColor: "warning",
      Icon: DoNotDisturb,
      label: "",
    },
    {
      title: "Migration Complexity",
      value: getComplexityLabel(dataResponse.content.migrationComplexity),
      color: "lightskyblue",
      bgColor: "pink",
      label: "",
      Icon: Troubleshoot,
    },
    { title: "Low Complexity", value: lowComplexity, color: getComplexityBGC(1), bgColor: "pink", label: "Low" },
    { title: "Mediun Complexity", value: midComplexity, color: getComplexityBGC(2), bgColor: "pink", label: "Mid" },
    { title: "High Complexity", value: highComplexity, color: getComplexityBGC(3), bgColor: "pink", label: "High" },
  ];

  // Functions to close drawers
  const handleCloseComments = () => {
    setCommentsOpen(false);
    setSelectedComments([]);
  };

  const handleCloseErrors = () => {
    setErrorsOpen(false);
    setSelectedSourceUploadComments(null);
  };

  // Function to handle download of target files
  const handleDownloadTargetFile = (file: ApiFile) => {
    if (!file.apiDestinationFileName) {
      console.error("Summary - Target file name not valid.");
      return;
    }
    console.log(`Summary - handleDownloadTargetFile for file : ${file.apiDestinationFileName}`);

    onDownload("TARGET", file.apiDestinationFileName);
  };

  /**
   * Function para descargar archivo desde General Information Source
   * @param fileName
   * @returns
   */
  const handleDownloadMigrationFile = (fileName: string) => {
    if (!fileName) {
      console.error("Summary - Source file name not valid.");
      return;
    }
    console.log(`Summary - handleDownloadMigrationFile for file : ${fileName}`);
    onDownload("MIGRATION", fileName);
  };

  // Function para descargar todos los archivos desde General Information Destination
  const handleDownloadAllTargetFiles = async () => {
    setIsDownloadingTarget(true);
    try {
      // Check if there are files with apiDestinationFileName
      const downloadableFiles = files.filter(
        (file) => file.apiDestinationFileName && file.apiDestinationFileName.trim() !== ""
      );

      if (downloadableFiles.length > 0) {
        const downloadPromises = downloadableFiles.map((file) => {
          if (file.apiName) {
            return new Promise((resolve) => {
              console.log(`Summary - Downloading migrated file: ${file.apiName}`);
              handleDownloadTargetFile(file);
              setTimeout(resolve, 500);
            });
          }
          return Promise.resolve();
        });
        await Promise.all(downloadPromises);
      } else {
        console.error("Summary - No APIs available for download");
      }
    } catch (error) {
      console.error("Summary - Error downloading migrated files:", error);
    } finally {
      setTimeout(() => setIsDownloadingTarget(false), 1000);
    }
  };

  /**
   * Funcion para llamar el listado de migraciones
   */
  const handleListMigration = () => {
    setSortedFiles(handleSortByApiName(files.filter((item) => item.excluded !== true)));
    setIsDataAssessmentListButtonDisabled(false);
    setIsDataMigrateListButtonDisabled(true);
    const headers: any = [
      { key: "apiName", label: "API Name" },
      { key: "apiSourceFileName", label: "Original File" },
      { key: "apiDestinationFileName", label: "Migrated File" },
      { key: "status", label: "Status" },
      { key: "comments", label: "LLM Comments" },
      { key: "sourceUploadComments", label: "Target System Comments" },
    ];
    setHeadersCustomTable(headers);
  };

  /**
   * Funcion para llamar el listad de assessment
   */
  const handleListAssessment = () => {
    const newDataAssessment = files.map((api) => ({
      ...api.assessmentContentDto,
      apiName: api.apiName,
      excluded: api.excluded,
      candidate: "Yes",
      sourceFileName: api.apiSourceFileName,
    }));
    console.log("newDataAssessment", newDataAssessment);

    setSortedFiles(handleSortByApiName(newDataAssessment));
    setIsDataAssessmentListButtonDisabled(true);
    setIsDataMigrateListButtonDisabled(false);
    const headers: any = [
      { key: "apiName", label: "API Name" },
      { key: "complexity", label: "Complexity" },
      { key: "candidate", label: "Candidate to migrate" },
      { key: "excluded", label: "Exclude" },
      { key: "detail", label: "Detail" },
    ];
    setHeadersCustomTable(headers);
  };

  /**
   *  Funcion para ordenar alfabeticamente
   * .slice para no modificar arreglo original
   * localeComare con numeric true para ordenar correctamente: ej: 9, 10, 11 y no 99 pase a 10 por ejemplo.
   * @param data arreglo de apis
   * @returns
   */
  const handleSortByApiName = (data: any) => {
    return data.slice().sort((a: any, b: any) => {
      const nameA = a.apiName || "";
      const nameB = b.apiName || "";
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });
  };

  return (
    <>
      {/* General Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, color: "#414141" }}>
          General Information
        </Typography>
        <Grid container spacing={2} sx={{ p: 2, mb: 2 }}>
          <Grid item xs={12} md={5}>
            <Box sx={{ border: 1, borderColor: "#E8E8E8", borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Source
              </Typography>
              <Typography variant="body2" gutterBottom color="#666666">
                System
              </Typography>
              <Typography variant="body1" gutterBottom>
                {getNameProxyManagers(
                  dataConfig.content,
                  "proxy_managers_source",
                  dataResponse.content.sourceApiManager
                )}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" component="span">
                  {dataResponse?.content?.sourceFileName}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ ml: 2, borderColor: "#E8E8E8", color: "#666666" }}
                  onClick={() => handleDownloadMigrationFile(dataResponse?.content?.sourceFileName!)}
                  disabled={isDownloading}
                >
                  {isDownloading ? "Downloading..." : "Download"}
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box
              sx={{
                border: 1,
                borderColor: "#E8E8E8",
                borderRadius: "50%",
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& svg": {
                  fontSize: 22,
                },
              }}
            >
              <ArrowForwardIcon />
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box sx={{ border: 1, borderColor: "#E8E8E8", borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Destination
              </Typography>
              <Typography variant="body2" gutterBottom color="#666666">
                System
              </Typography>
              <Typography variant="body1" gutterBottom>
                {getNameProxyManagers(
                  dataConfig.content,
                  "proxy_managers_target",
                  dataResponse.content.targetApiManager
                )}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" component="span">
                  All target files
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ ml: 2, borderColor: "#E8E8E8", color: "#666666" }}
                  onClick={handleDownloadAllTargetFiles}
                  disabled={
                    isDownloadingTarget ||
                    !files.some((file) => file.apiDestinationFileName && file.apiDestinationFileName.trim() !== "")
                  }
                >
                  {isDownloadingTarget ? "Downloading..." : "Download"}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2, margin: 2 }}>
          <TextField label="Provider" value={aiModel.provider} disabled fullWidth />
          <TextField label="Model" value={aiModel.model} disabled fullWidth />
          <TextField label="Region" value={aiModel.region} disabled fullWidth />
          <TextField label="Temperature" value={aiModel.temperature} disabled fullWidth />
        </Box>
      </Paper>
      {/* New Apis */}
      <Paper sx={{ p: 3, mb: 2 }}>
        {/* <Paper> */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" sx={{ mb: 3, color: "#414141" }}>
              Assessment Migrated
            </Typography>
            <Grid container spacing={2}>
              {dataInfoBox.map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <InfoBox
                    title={item.title}
                    value={item.value}
                    color={item.color}
                    bgColor={item.bgColor}
                    Icon={item.Icon!}
                    label={item.label!}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Migration Distribution
            </Typography>
            <MigrationDistributionChart data={chartData} />
          </Grid>
        </Grid>
      </Paper>

      {/* Data table parametrizable listado de migrate y assessment*/}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "left",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h6">API List {isDataMigrateListButtonDisabled! ? "Migrate" : "Assessment"}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleListMigration}
              disabled={isDataMigrateListButtonDisabled}
            >
              Migration
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleListAssessment}
              disabled={isDataAssessmentListButtonDisabled}
            >
              Assessment
            </Button>
          </Box>
        </Box>
        <TableContainer>
          <CustomTable
            columns={headersCustomTable}
            data={sortedFiles}
            download={onDownload}
            migrationId={migrationId}
          ></CustomTable>
        </TableContainer>
      </Paper>

      {/* Comments Drawer */}
      <Drawer
        anchor="right"
        open={commentsOpen}
        onClose={handleCloseComments}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 400 },
            p: 2,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            pb: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="h6">Comments for {selectedApiName}</Typography>
          <IconButton onClick={handleCloseComments}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Migration Comments */}
        {selectedComments.length > 0 ? (
          <List>
            {selectedComments.map((comment, index) => (
              <ListItem key={index} sx={{ flexDirection: "column", alignItems: "flex-start", mb: 2 }}>
                <ListItemText
                  primary={`Comment ${index + 1}`}
                  primaryTypographyProps={{ fontWeight: "bold" }}
                  secondary={<Box sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: comment }} />}
                />
                {index < selectedComments.length - 1 && <Divider sx={{ width: "100%", mt: 2 }} />}
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No migration comments available for this API.
            </Typography>
          </Box>
        )}
      </Drawer>
      {/* Errors Drawer */}
      <Drawer
        anchor="right"
        open={errorsOpen}
        onClose={handleCloseErrors}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 450 },
            p: 2,
            bgcolor: "#FFFDF7",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            pb: 2,
            borderBottom: 1,
            borderColor: "warning.main",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", maxWidth: "calc(100% - 40px)" }}>
            <WarningIcon sx={{ color: "#F57C00", mr: 1, flexShrink: 0 }} />
            <Typography
              variant="h6"
              sx={{
                color: "warning.main",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Upload Status for {selectedApiName}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseErrors} sx={{ flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Source Upload Comments */}
        {selectedSourceUploadComments && selectedSourceUploadComments.length > 0 ? (
          <List sx={{ p: 0 }}>
            {selectedSourceUploadComments.map((comment, index) => (
              <ListItem
                key={index}
                sx={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  mb: 2,
                  bgcolor: "#FFF8E1",
                  borderRadius: 1,
                  p: 2,
                  border: "1px solid",
                  borderColor: "warning.light",
                  overflowWrap: "break-word",
                  wordWrap: "break-word",
                  wordBreak: "break-word",
                  hyphens: "auto",
                }}
              >
                <Box sx={{ width: "100%", mb: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "medium",
                      overflowWrap: "break-word",
                      wordWrap: "break-word",
                      wordBreak: "break-word",
                      hyphens: "auto",
                    }}
                  >
                    {comment.message}
                  </Typography>
                </Box>

                <Box sx={{ width: "100%", mb: 1 }}>
                  <Typography variant="caption" sx={{ color: "#F57C00" }}>
                    Code: {comment.code} | Status: {comment.status}
                  </Typography>
                </Box>

                {comment.details && comment.details.length > 0 && (
                  <Box sx={{ width: "100%" }}>
                    <Typography variant="body2" sx={{ fontWeight: "medium", mb: 0.5 }}>
                      Details:
                    </Typography>
                    <List dense sx={{ pl: 2 }}>
                      {comment.details.map((detail: SourceUploadCommentDetail, detailIndex) => (
                        <ListItem
                          key={detailIndex}
                          sx={{
                            display: "list-item",
                            listStyleType: "disc",
                            p: 0,
                            pl: 1,
                            mb: 0.5,
                            overflowWrap: "break-word",
                            wordWrap: "break-word",
                            wordBreak: "break-word",
                            hyphens: "auto",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              overflowWrap: "break-word",
                              wordWrap: "break-word",
                              wordBreak: "break-word",
                              hyphens: "auto",
                            }}
                          >
                            {detail["@type"]}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {index < selectedSourceUploadComments.length - 1 && <Divider sx={{ width: "100%", mt: 2 }} />}
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 2, textAlign: "center", bgcolor: "#FFF8E1", borderRadius: 1 }}>
            <Typography variant="body1" color="#F57C00">
              No upload status comments available for this API.
            </Typography>
          </Box>
        )}
      </Drawer>
    </>
  );
};

/**
 * Funcion para setear el nombre del provider
 * @param objConfig servicio de configuration guardado en localstorage
 * @param objRes provider a validar
 * @returns
 */
export function getNameConfigurationProvider(objConfig: any, objRes: any) {
  const provider = objConfig.llm_providers.find((item: any) => item.id === objRes.provider);
  return provider.name || "";
}

/**
 * Funcion para setear el nombre del provider
 * @param objConfig servicio de configuration guardado en localstorage
 * @param objRes provider a validar
 * @returns
 */
export function getNameConfigurationModel(objConfig: any, objRes: any) {
  const provider = objConfig.llm_providers.find((item: any) => item.id === objRes.provider);
  const model = provider.models.find((item: any) => item.id === objRes.model);
  return model.name || "";
}

/**
 * Metodo para setear el nombre de los managers.. IBO o APIGEE segun el servicio de configurations
 * @param objConfig servicio de configuration guardado en localstorage
 * @param objKeyConfig key del objeto del servicio de configuration
 * @param idName name a validar y mapear.
 * @returns
 */
export function getNameProxyManagers(objConfig: any, objKeyConfig: any, idName: any) {
  const managerName = objConfig[objKeyConfig].find((item: any) => item.id === idName.id);
  return managerName.name || "";
}

export default Summary;
